<template>
  <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mx-4 sm:mx-12">
    <div class="flex items-center justify-between gap-x-4 w-full pb-2">
      <span class="font-semibold text-blue-600 text-2xl shrink-0">Delivery</span>
      <transition
        name="quick-fade"
        mode="out-in"
        v-if="canUpdateDeliveries"
      >
        <BatchModifierControls
          v-if="currentBatchAction"
          :label="currentBatchAction.label"
          :disabled="submittingBatch || !currentBatchAction.canSubmit"
          :submitting="submittingBatch"
          @cancel="batchActionStop"
          @submit="batchActionSubmit"
        />
        <IndexActionMenu v-else>
          <MenuItem
            as="button"
            class="text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-3 text-sm w-full"
            v-for="action in batchActions"
            @click="batchActionStart(action)"
          >
            {{ action.label }}
          </MenuItem>
        </IndexActionMenu>
      </transition>
    </div>
  </div>

  <div class="w-full flex flex-col h-full overflow-y-auto">
    <div class="flex flex-col md:flex-row gap-1 py-2 px-4 sm:px-12">
      <div class="grid grid-cols-12 gap-1 md:w-4/12">
        <div class="order-last sm:order-first col-span-3">
          <SavedFiltersMenu
            :saved-filters="usersSavedFilters.data.value"
            @save="({ filterName }) => usersSavedFilters.save({ filterName })"
            @load="usersSavedFilters.load"
            @delete="usersSavedFilters.remove"
          />
        </div>
        <div class="col-span-5">
          <LocationsMenu
            :locations="page.props.auth.locations.data"
            @selectLocation="selectedLocationId = $event.id"
            :selected-location-id="selectedLocationId"
          />
        </div>
        <div class="col-span-4">
          <CalendarFilter v-model="calendarFilterDate" />
        </div>
      </div>
      <div>
        <SavedFiltersReset
          :active="!stickyFilters.isInDefaultState.value"
          @clear-all="resetTableToDefault"
        />
      </div>
    </div>

    <AgGridVue
      v-bind="gridOptions"
      class="ag-theme-alpine salve-theme-delivery"
    />
  </div>

  <AssignModal
    :show="assignModal.state.show"
    :can-assign-driver-one="assignModal.state.canAssignDriverOne"
    :driver-one-list="assignModal.state.driverOneList"
    @close="assignModal.onClose"
    @assign="assignModal.onAssign"
    :vehicles="page.props.vehicles || []"
    :drivers="page.props.drivers || []"
  />
</template>

<script setup>
import { computed, ref, watch, reactive, readonly, nextTick } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'
import { router } from '@inertiajs/vue3'
import { settle } from '@/utils/promises'
import { usePoll } from '@/Composables/poll'
import { SYNC_DELIVERIES_TABLE_MS } from '@/Pages/Deliveries/constants'
import DollarCell from '@/Components/DataGrid/Cells/DollarCell.vue'
import { useMobileWidths } from '@/Components/DataGrid/Composables/mobile-widths'
import { usePage } from '@inertiajs/vue3'
import LocationsMenu from '@/Components/DataGrid/Filters/LocationsMenu.vue'
import { useAccessUser } from '@/Composables/access-user'
import OrderStatusCell from '@/Pages/Deliveries/DataGrid/OrderStatusCell.vue'
import { DELIVERY_STATUS, DELIVERY_STATUS_LABELS } from '@/Pages/Deliveries/constants'
import { DateTime } from 'luxon'
import LinkCell from '@/Components/DataGrid/Cells/LinkCell.vue'
import IndexActionMenu from '@/Components/IndexActionMenu.vue'
import AssignModal from '@/Pages/Deliveries/Components/AssignModal.vue'
import { api } from '@/Pages/Deliveries/api'
import { LANG } from '@/Pages/Deliveries/constants'
import { useErrors } from '@/Composables/errors'
import CalendarFilter from '@/Components/DataGrid/Filters/CalendarFilter.vue'
import SavedFiltersMenu from '@/Components/DataGrid/Filters/SavedFiltersMenu.vue'
import { useUsersSavedFilters, useStickyFilters } from '@/Components/DataGrid/Composables/saved-filters'
import { isToday, isTomorrow } from '@/utils/datetime'
import { VERIFICATION_STATUS } from '@/Pages/Deliveries/constants'
import { useBatchModifier } from '@/Components/DataGrid/Composables/batch-modifiers'
import { MenuItem } from '@headlessui/vue'
import BatchModifierControls from '@/Components/BatchModifierControls.vue'
import { useGridState } from '@/Components/DataGrid/Composables/grid-state'
import { useFilterStateManagement } from '@/Components/DataGrid/Composables/filter-state'
import { ROLE_DRIVER } from '@/utils/roles'
import VerifiedCell from '@/Pages/Deliveries/DataGrid/VerifiedCell.vue'
import { useSortStateManagement } from '@/Components/DataGrid/Composables/sort-state'
import { updateColumnSort } from '@/Components/DataGrid/utils'
import { useGridApi } from '@/Components/DataGrid/grid-api'
import ColumnSum from '@/Components/DataGrid/Headers/ColumnSum.vue'
import CheckmarkCell from '@/Components/DataGrid/Cells/CheckmarkCell.vue'
import SavedFiltersReset from '@/Components/DataGrid/Filters/SavedFiltersReset.vue'
import { wrapWithFlag, createFlagColumn } from '@/Components/Flags'
import { useCompanyPermissions } from '@/Composables/permissions'
import { PERMISSIONS } from '@/constants'

const SAVED_FILTERS_KEY = 'deliveries'

const { gridApi, createGridOptions } = useGridApi()
const orderCounts = ref([])
const mergedRows = ref([])
const tableInitialized = ref(false)
const activeSync = ref(null)
const calendarFilterDate = ref(null)

const page = usePage()
const { currentLocation } = useAccessUser()
const { showError } = useErrors()
const { context: tableContext } = useGridState()
const permissions = useCompanyPermissions()

useFilterStateManagement({ gridApi: readonly(gridApi) })
useSortStateManagement({ gridApi: readonly(gridApi) })

const selectedLocationId = ref(currentLocation.value.id)

const redrawMergedRows = () => {
  if (mergedRows.value.length > 0 && tableInitialized.value) {
    const rowNodes = mergedRows.value.map((row) => gridApi.value.getRowNode(row.id))
    gridApi.value.redrawRows({ rowNodes })
  }
}

// Handle any custom filters to be saved with regular AG grid filters.
const onFiltersSave = () => {
  const filters = {
    'delivery.fulfillment_location_id': { value: selectedLocationId.value }
  }
  if (calendarFilterDate.value) {
    if (isToday(calendarFilterDate.value)) {
      filters['to_fulfill_at'] = { value: 'Today' }
    } else if (isTomorrow(calendarFilterDate.value)) {
      filters['to_fulfill_at'] = { value: 'Tomorrow' }
    }
  }
  return filters
}

// When loading a saved filter, any settings that require new table data
// to load should be done here, in the pre load hook.
const onFiltersPreLoad = async (data) => {
  const locationId = data.state.filters.custom['delivery.fulfillment_location_id']

  if (locationId && locationId.value !== selectedLocationId.value) {
    selectedLocationId.value = locationId.value
  }

  return nextTick(() => {
    return activeSync.value
  })
}

// After the saved filter has been applied to the table, we can apply
// any additional custom filters here in the post load hook.
const onFiltersPostLoad = async (data) => {
  const customDateFilter = data.state.filters.custom['to_fulfill_at']
  const agGridDateFilter = data.state.filters.agGrid['to_fulfill_at']

  if (customDateFilter) {
    if (customDateFilter.value === 'Today') {
      calendarFilterDate.value = new Date()
    }
    if (customDateFilter.value === 'Tomorrow') {
      calendarFilterDate.value = DateTime.now().plus({ days: 1 }).startOf('day').toJSDate()
    }
  } else if (agGridDateFilter) {
    calendarFilterDate.value = new Date(agGridDateFilter.dateFrom)
  } else {
    calendarFilterDate.value = null
  }
}

const resetTableToDefault = () => {
  // Stop any batch actions that are running.
  if (currentBatchAction.value) {
    batchActionStop()
  }
  // Clear AG grid filters.
  gridApi.value.setFilterModel({})
  // Set column sorting to default.
  updateColumnSort(gridApi, gridOptions.value.columnDefs)
  // Clear calendar filter.
  calendarFilterDate.value = null
  // Reset selected location to the user's default "current" location.
  selectedLocationId.value = currentLocation.value.id
}

const usersSavedFilters = useUsersSavedFilters({
  key: SAVED_FILTERS_KEY,
  gridApi: readonly(gridApi),
  onSave: onFiltersSave,
  onPreLoad: onFiltersPreLoad,
  onPostLoad: onFiltersPostLoad
})

const stickyFilters = useStickyFilters({
  key: SAVED_FILTERS_KEY,
  gridApi: readonly(gridApi),
  onSave: onFiltersSave,
  onPreLoad: onFiltersPreLoad,
  onPostLoad: onFiltersPostLoad
})

watch(calendarFilterDate, (jsDate) => {
  const dateString = DateTime.fromJSDate(jsDate).toFormat('yyyy-MM-dd HH:mm:ss')
  const currentFilters = gridApi.value.getFilterModel()

  gridApi.value.setFilterModel({
    ...currentFilters,
    to_fulfill_at: {
      dateFrom: dateString,
      dateTo: null,
      filterType: 'date',
      type: 'equals'
    }
  })
})

watch(selectedLocationId, async () => {
  await hardRefreshTable()
})

// Persist the filter state to storage any time it changes.
watch(
  [selectedLocationId, calendarFilterDate, () => tableContext.activeFilters, () => tableContext.activeSort],
  async () => {
    await stickyFilters.save()
    redrawMergedRows()
  },
  {
    deep: true
  }
)

watch(
  gridApi,
  async () => {
    // Load the sticky filter when the grid is initialized
    await stickyFilters.load()

    if (!tableInitialized.value) {
      await syncDeliveriesTable()
    }
    syncDeliveriesTablePoll.start()
  },
  {
    once: true
  }
)

const syncDeliveriesTable = async () => {
  activeSync.value = new Promise(async (resolve) => {
    api.cancelFetchDeliveries()
    const [err, result] = await settle(api.fetchDeliveries({ locationId: selectedLocationId.value }))

    if (err) {
      if (err.name !== 'AbortError') {
        showError({ message: LANG.FAILED_TO_FETCH_DELIVERIES, useToast: true })
      }
      resolve('error')
      return
    }

    const [_, data] = result

    const counts = []
    const merged = []

    for (const row of data.rows) {
      const orderCount = row.delivery.order_count ?? 1
      counts.push([row.id, orderCount])

      if (orderCount > 1) {
        merged.push({ id: row.id })
      }
    }

    orderCounts.value = counts
    mergedRows.value = merged

    gridApi.value.setGridOption('rowData', data.rows)

    redrawMergedRows()

    tableInitialized.value = true

    resolve('success')
  })
  return activeSync.value
}

const syncDeliveriesTablePoll = usePoll(syncDeliveriesTable, { interval: SYNC_DELIVERIES_TABLE_MS })

const hardRefreshTable = async () => {
  syncDeliveriesTablePoll.stop()
  await syncDeliveriesTable()
  syncDeliveriesTablePoll.start()
}

const assignModal = {
  state: reactive({
    show: false,
    selectedIds: [],
    canAssignDriverOne: false,
    allDriverOnes: new Set(),
    driverOneList: ''
  }),

  resolve: null,

  open: async (selectedIds) => {
    assignModal.state.show = true
    assignModal.state.selectedIds = selectedIds
    assignModal.state.allDriverOnes = new Set()
    assignModal.state.driverOneList = ''
    assignModal.state.canAssignDriverOne = false

    const selectedNodes = gridApi.value.getSelectedNodes()
    const statuses = new Set(selectedNodes.map((node) => node.data.delivery.status))

    assignModal.state.canAssignDriverOne = statuses.isSubsetOf(
      new Set([DELIVERY_STATUS.PENDING, DELIVERY_STATUS.RESCHEDULED])
    )

    if (!assignModal.state.canAssignDriverOne) {
      selectedNodes.forEach((node) => {
        const driverName = getDriverName(node.data.delivery.drivers, 0)
        if (driverName) {
          assignModal.state.allDriverOnes.add(driverName)
        }
      })
    }

    assignModal.state.driverOneList = Array.from(assignModal.state.allDriverOnes).join(', ')

    return new Promise((resolve) => {
      assignModal.resolve = resolve
    })
  },

  onClose: () => {
    assignModal.resolve('closed')
    assignModal.state.show = false
  },

  onAssign: async (vehicleId, driverOneId, driverTwoId) => {
    const [err, _] = await settle(
      api.assignVehicleDriver(assignModal.state.selectedIds, vehicleId, driverOneId, driverTwoId)
    )

    assignModal.resolve()
    assignModal.state.show = false

    if (err) {
      showError({ message: LANG.FAILED_TO_ASSIGN_VEHICLE_DRIVER, useToast: true })
      return
    }
  }
}

const { currentBatchAction, submittingBatch, batchActionStart, batchActionStop, batchActionSubmit, onSubmitBatch } =
  useBatchModifier(gridApi)

onSubmitBatch(hardRefreshTable)

const mapByDeliveryId = (node) => node.data.delivery_id

const batchActions = [
  {
    label: 'Merge Orders',
    actionFn: api.mergeDeliveries,
    actionMapper: mapByDeliveryId,
    minSelectedRows: 2,
    requiresConfirmation: true,
    actionDescription: 'Are you sure you want to merge these orders?'
  },
  {
    label: 'Assign Vehicle / Driver',
    actionFn: assignModal.open,
    actionMapper: mapByDeliveryId
  },
  {
    label: 'Dispatch Orders',
    actionFn: api.dispatchDeliveries,
    actionMapper: mapByDeliveryId,
    requiresConfirmation: true,
    actionDescription: 'Are you sure you want to dispatch these orders?'
  },
  {
    label: 'Notify Orders',
    actionFn: api.notifyOrders,
    actionMapper: mapByDeliveryId,
    requiresConfirmation: true,
    actionDescription: 'Are you sure you want to notify these orders?'
  },
  {
    label: 'Complete Deliveries',
    actionFn: api.completeOrders,
    actionMapper: mapByDeliveryId,
    requiresConfirmation: true,
    actionDescription: 'Are you sure you want to complete these orders? This action can NOT be undone!'
  },
  {
    label: 'Print Manifests',
    actionFn: (orderIds) => {
      open(route('orders.manifests', { orders: orderIds }), '_blank')
    }
  },
  {
    label: 'Print Routes',
    actionFn: (orderIds) => {
      open(route('deliveries.top-sheet', { orders: orderIds }), '_blank')
    }
  },
  {
    label: 'Cancel Orders',
    requiresConfirmation: true,
    actionDescription: 'Are you sure you want to cancel these orders?',
    actionFn: api.cancelDeliveries
  }
]

const getDriverName = (driver) => {
  if (!driver) {
    return null
  }
  const firstName = driver.first_name || ''
  const lastName = driver.last_name || ''
  return `${firstName} ${lastName}`.trim()
}

const isDriverRole = computed(() => {
  return page.props.auth?.company_role === ROLE_DRIVER
})

const canUpdateDeliveries = computed(() => {
  return permissions.userCan(PERMISSIONS.UPDATE_DELIVERIES)
})

// Setup Grid Options
const gridOptions = createGridOptions({
  onGridReady: (params) => {
    gridApi.value = params.api
  },

  getRowId: (params) => params.data.id,

  columnDefs: [
    {
      headerName: '',
      field: 'checkbox',
      width: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      hide: true
    },
    {
      headerName: 'Verified',
      field: 'verification_status',
      pinned: 'left',
      width: 80,
      desktopWidth: 120,
      cellRenderer: VerifiedCell,
      cellClass: (params) => {
        return params.value ? 'text-green-400' : 'text-gray-400'
      },
      valueGetter: (params) => {
        return params.data.verification_status === VERIFICATION_STATUS.VERIFIED
      },
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [true, false],
        cellRenderer: (params) => (params.value ? 'Yes' : 'No'),
        suppressSelectAll: true,
        suppressMiniFilter: true,
        defaultToNothingSelected: false,
        excelMode: 'mac'
      }
    },
    {
      headerName: 'Status',
      field: 'delivery.status',
      filter: true,
      cellRenderer: OrderStatusCell,
      pinned: 'left',
      cellClass: (params) => {
        return {
          [DELIVERY_STATUS.PENDING]: 'bg-yellow-200 text-yellow-600',
          [DELIVERY_STATUS.READY]: 'bg-gray-300 text-gray-700',
          [DELIVERY_STATUS.DISPATCHED]: 'bg-blue-200 text-blue-400',
          [DELIVERY_STATUS.IN_TRANSIT]: 'bg-blue-400 text-blue-200',
          [DELIVERY_STATUS.COMPLETE]: 'bg-green-200 text-green-400',
          [DELIVERY_STATUS.CANCELLED]: 'bg-red-200 text-red-400',
          [DELIVERY_STATUS.RESCHEDULED]: 'bg-yellow-200 text-yellow-600',
          [DELIVERY_STATUS.FAILED]: 'bg-red-400 text-red-200'
        }[params.data.delivery.status]
      },
      valueGetter: (params) => {
        return `${params.data.delivery.status}-${params.data.created_at}`
      },
      filterValueGetter: (params) => {
        return DELIVERY_STATUS_LABELS[params.data.delivery.status]
      },
      cellRendererParams: (params) => ({
        label: DELIVERY_STATUS_LABELS[params.data.delivery.status],
        status: params.data.delivery.status,
        link: route('deliveries.show', { id: params.data.id })
      }),
      width: 130,
      desktopWidth: 190
    },
    {
      headerName: '#',
      field: 'delivery.stop_number',
      width: 70,
      valueFormatter: (params) => {
        return params.data.delivery.stop_number ?? '--'
      },
      valueGetter: (params) => {
        return params.data.delivery.stop_number ?? null
      },
      sortable: true,
      sort: 'asc',
      sortIndex: 0,

      comparator: (valueA, valueB, _nodeA, _nodeB, isDescending) => {
        const isEmptyA = valueA === null
        const isEmptyB = valueB === null

        if (isEmptyA && isEmptyB) return 0

        if (isEmptyA) return isDescending ? -1 : 1
        if (isEmptyB) return isDescending ? 1 : -1

        return valueA - valueB
      }
    },
    {
      headerName: 'ID',
      field: 'reference_number',
      width: 150,
      filter: 'agTextColumnFilter',
      cellRenderer: wrapWithFlag(LinkCell),
      cellRendererParams: (params) => ({
        showFlag: Boolean(params.data.flags?.length),
        url: route('orders.show', { id: params.data.id })
      }),
      sortable: false
    },
    {
      headerName: 'Customer',
      field: 'customer_name',
      width: 200,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        return `${params.data.customer_name}-(${params.data.delivery.order_count})`
      },
      cellRenderer: (params) => {
        return params.data.customer_name
      },
      rowSpan: (params) => {
        // If the current row has multiple orders, look ahead for additional rows
        // with multiple orders to determine the total span length.
        let lookAhead = params.data.delivery.order_count > 1

        // However, if the previous row is visible and has multiple orders, then
        // we don't need to look ahead. This row is part of the previous row's span.
        if (lookAhead) {
          const previousRow = params.api.getDisplayedRowAtIndex(params.node.rowIndex - 1)
          if (previousRow && previousRow.displayed && (previousRow.data.delivery.order_count ?? 1) > 1) {
            lookAhead = false
          }
        }

        if (!lookAhead) {
          return 1
        }

        // Starting from the next row after the current row, count how many visible rows
        // have multiple orders. This is the total row span length.
        const startIndex = orderCounts.value.findIndex(([id]) => id === params.data.id)
        let rowsToSpan = 1

        for (let i = startIndex + 1; i < orderCounts.value.length; i++) {
          const [id, count] = orderCounts.value[i]
          if (count === 1) {
            break
          }
          const row = params.api.getRowNode(id)
          if (row.displayed) {
            rowsToSpan++
          }
        }
        return rowsToSpan
      },
      cellClass: (params) => {
        return params.data.delivery.order_count > 1 ? 'bg-gray-200 !border-4 !border-gray-400' : ''
      }
    },
    {
      headerName: 'Region',
      field: 'region_name',
      width: 150,
      filter: 'agSetColumnFilter'
    },
    {
      headerName: 'Delivery Date',
      field: 'to_fulfill_at',
      width: 150,
      filter: 'agDateColumnFilter',
      valueGetter: (params) => {
        return params.data.to_fulfill_at
          ? DateTime.fromISO(params.data.to_fulfill_at).startOf('day').toFormat('MM/dd/yyyy')
          : '--'
      },
      filterValueGetter: (params) => {
        return params.data.to_fulfill_at ? DateTime.fromISO(params.data.to_fulfill_at).startOf('day').toJSDate() : null
      },
      floatingFilter: false
    },
    {
      headerName: 'ETA',
      field: 'delivery.estimated_delivery_at',
      width: 150,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        return params.data.delivery.estimated_delivery_at
          ? DateTime.fromISO(params.data.delivery.estimated_delivery_at).toFormat('h:mm a')
          : '--'
      }
    },
    {
      headerName: 'Car',
      field: 'delivery.vehicle.name',
      width: 150,
      filter: true
    },
    {
      headerName: 'Driver 1',
      field: 'delivery.driver_one',
      valueGetter: (params) => getDriverName(params.data.delivery?.driver_one) ?? 'Unassigned',
      width: 150,
      filter: true
    },
    {
      headerName: 'Driver 2',
      field: 'delivery.driver_two',
      valueGetter: (params) => getDriverName(params.data.delivery?.driver_two) ?? 'Unassigned',
      width: 150,
      filter: true
    },
    {
      headerName: 'Item Count',
      field: 'item_count',
      width: 150,
      sortable: false
    },
    {
      headerName: 'Total',
      field: 'total',
      width: 130,
      cellRenderer: DollarCell,
      sortable: false,

      filter: 'agTextColumnFilter',
      suppressFloatingFilterButton: true,
      floatingFilterComponent: ColumnSum,
      floatingFilterComponentParams: {
        type: 'floatingFilter'
      }
    },
    {
      headerName: 'Payment Type',
      field: 'payment_type',
      width: 150,
      sortable: false
    },
    {
      headerName: 'Delivery Fee',
      field: 'total_fees',
      width: 150,
      cellRenderer: DollarCell,
      sortable: false
    },
    {
      headerName: 'Order Notes',
      field: 'order_notes',
      width: 150,
      sortable: false
    },
    {
      headerName: 'Employee?',
      field: 'customer.is_employee',
      cellRenderer: CheckmarkCell,
      width: 120
    },
    {
      headerName: 'View on Onfleet',
      field: 'delivery.tracking_url',
      width: 150,
      sortable: false,
      cellRenderer: LinkCell,
      cellRendererParams: (params) => ({
        url: params.data.delivery.tracking_url,
        display: 'View',
        external: true
      })
    },
    createFlagColumn({ clientSideModel: true })
  ],

  defaultColDef: {
    floatingFilter: true,
    suppressHeaderMenuButton: true
  },

  suppressMovableColumns: true,
  suppressRowTransform: true, // @Note: required for rowSpan feature.
  pagination: false,
  animateRows: false,
  sideBar: null,
  rowSelection: 'multiple'
})

useMobileWidths({ gridApi: readonly(gridApi), gridOptions })
</script>
